import classNames from 'classnames';

import { EditableTitle } from '@utilities';

import {
  getScreenerPromptLabel,
  type HierarchyPromptKey,
  SCREENER_PROMPT_TOKEN_REGEX,
  toScreenerPromptTestId,
} from '../../../../utilities';
import HierarchyPromptInput from '../HierarchyPromptInput';

import styles from './_index.module.scss';

interface PromptTextProps {
  hierarchyPromptOptions: Partial<Record<HierarchyPromptKey, string[]>>;
  missingPromptKeys: string[];
  onPromptChange: (promptKey: string, value: string) => void;
  promptValues: Record<string, string>;
  template: string;
}

const PromptText = ({
  hierarchyPromptOptions,
  missingPromptKeys,
  onPromptChange,
  promptValues,
  template,
}: PromptTextProps) => {
  const nodes = [];
  let lastIndex = 0;
  let match;

  SCREENER_PROMPT_TOKEN_REGEX.lastIndex = 0;
  while ((match = SCREENER_PROMPT_TOKEN_REGEX.exec(template)) !== null) {
    const [fullMatch, promptKey] = match;
    const index = match.index;

    if (index > lastIndex) {
      nodes.push(<span key={`text-${lastIndex}`}>{template.slice(lastIndex, index)}</span>);
    }

    const promptValue = promptValues[promptKey]?.trim() ? promptValues[promptKey] : '';
    const placeholder = getScreenerPromptLabel(promptKey);
    const hierarchyOptions = hierarchyPromptOptions[promptKey as HierarchyPromptKey];

    nodes.push(
      <div
        className={classNames(styles['prompt-text-wrapper'], {
          [styles['prompt-text-error']]: missingPromptKeys.includes(promptKey),
        })}
        data-testid={toScreenerPromptTestId(promptKey)}
        key={`prompt-${promptKey}-${index}`}
      >
        {hierarchyOptions ? (
          <HierarchyPromptInput
            ariaInvalid={missingPromptKeys.includes(promptKey)}
            onChange={(value) => onPromptChange(promptKey, value)}
            options={hierarchyOptions}
            placeholder={placeholder}
            promptKey={promptKey}
            value={promptValue}
          />
        ) : (
          <EditableTitle
            aria-invalid={missingPromptKeys.includes(promptKey)}
            className={styles['prompt-text']}
            onBlur={(value) => onPromptChange(promptKey, value)}
            onChange={(value) => onPromptChange(promptKey, value)}
            placeholder={placeholder}
            value={promptValue}
          />
        )}
      </div>
    );

    lastIndex = index + fullMatch.length;
  }

  if (lastIndex < template.length) {
    nodes.push(<span key={`text-${lastIndex}`}>{template.slice(lastIndex)}</span>);
  }

  return <>{nodes}</>;
};

export default PromptText;
